import { useState, useEffect, useCallback, useRef } from "react";
import UsuariosService from "../api/usuarios.api";

const MENSAJE_ERROR_CARGA = "No se pudieron cargar los usuarios";

/**
 * Hook para orquestar el listado (con filtro incluirBajas) y las operaciones
 * CRUD de usuarios, apoyándose en UsuariosService (api/usuarios.api.js), para
 * que la página quede como un orquestador de presentación.
 *
 * DECISION (condición de carrera, criterio 2): UsuariosService.list() usa
 * axios sin AbortController, así que en vez de intentar cancelar la petición
 * HTTP en vuelo se descarta su respuesta si ya no es la más reciente. Un
 * contador de secuencia (requestIdRef) se incrementa en cada llamada a
 * cargarUsuarios; al resolver (éxito o error) solo se aplica el resultado si
 * el id capturado en el cierre sigue siendo el último emitido. Así, si el
 * usuario alterna el switch de incluirBajas varias veces seguidas, gana
 * siempre la última petición lanzada, nunca la que responde antes por azar
 * de red — un simple "si loading, no lances otra" dejaría la lista
 * desincronizada del switch en ese escenario.
 *
 * DECISION (criterios 4 y 5, refresco tras CRUD): tras crear, actualizar,
 * dar de baja o restaurar, el hook vuelve a pedir la lista al servidor
 * (silenciosamente, sin activar el loading global) en lugar de actualizar el
 * array local a mano. Actualizar en local obligaría a duplicar aquí la regla
 * de negocio de "cuándo un usuario debe desaparecer o aparecer según
 * incluirBajas", que ya vive en el backend (GET /usuarios ?incluirBajas).
 * Pedir de nuevo la lista con el incluirBajas vigente resuelve ambos casos
 * gratis: con incluirBajas=false el usuario dado de baja deja de venir en la
 * respuesta y desaparece; con incluirBajas=true sigue viniendo, ya con su
 * fechaBaja actualizada.
 *
 * @returns {{
 *   usuarios: Array,
 *   loading: boolean,
 *   error: string|null,
 *   incluirBajas: boolean,
 *   setIncluirBajas: Function,
 *   reload: Function,
 *   crearUsuario: Function,
 *   actualizarUsuario: Function,
 *   darDeBaja: Function,
 *   restaurarUsuario: Function
 * }} Estado y operaciones del listado de usuarios
 */
export const useUsuarios = () => {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [incluirBajas, setIncluirBajas] = useState(false);

    const requestIdRef = useRef(0);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    /**
     * Carga (o recarga) la lista de usuarios desde el servidor con el
     * incluirBajas vigente. Con silent=true no toca el loading global (uso
     * interno tras un CRUD, criterio 4); con silent=false (por defecto) lo
     * activa durante la petición, para la carga inicial y las recargas
     * explícitas (criterio 3).
     */
    const cargarUsuarios = useCallback(async ({ silent = false } = {}) => {
        const requestId = ++requestIdRef.current;

        setError(null);
        if (!silent) {
            setLoading(true);
        }

        try {
            const data = await UsuariosService.list({ incluirBajas });

            // Una petición más reciente ya se lanzó, o el hook se desmontó
            // mientras esta estaba en vuelo: se descarta este resultado.
            if (requestId !== requestIdRef.current || !mountedRef.current) {
                return;
            }

            setUsuarios(data);
        } catch (err) {
            if (requestId !== requestIdRef.current || !mountedRef.current) {
                return;
            }
            console.error("Error al cargar usuarios:", err);
            setError(MENSAJE_ERROR_CARGA);
        } finally {
            if (!silent && requestId === requestIdRef.current && mountedRef.current) {
                setLoading(false);
            }
        }
    }, [incluirBajas]);

    // Carga inicial y recarga automática al cambiar incluirBajas
    useEffect(() => {
        cargarUsuarios();
    }, [cargarUsuarios]);

    const reload = useCallback(() => cargarUsuarios(), [cargarUsuarios]);

    // Las 4 operaciones devuelven la promesa de UsuariosService tal cual (la
    // UI la usa para su propio loading local en el modal, criterio 4) y solo
    // encadenan el refresco silencioso de la lista en éxito; si el service
    // lanza, la recarga no llega a ejecutarse y el error sube intacto a la UI
    // (crearUsuario en particular conserva isBaja/userId para TASK-012).
    const crearUsuario = useCallback(async (data) => {
        const usuario = await UsuariosService.create(data);
        await cargarUsuarios({ silent: true });
        return usuario;
    }, [cargarUsuarios]);

    const actualizarUsuario = useCallback(async (id, data) => {
        const usuario = await UsuariosService.update(id, data);
        await cargarUsuarios({ silent: true });
        return usuario;
    }, [cargarUsuarios]);

    const darDeBaja = useCallback(async (id) => {
        const usuario = await UsuariosService.delete(id);
        await cargarUsuarios({ silent: true });
        return usuario;
    }, [cargarUsuarios]);

    const restaurarUsuario = useCallback(async (id) => {
        const usuario = await UsuariosService.restaurar(id);
        await cargarUsuarios({ silent: true });
        return usuario;
    }, [cargarUsuarios]);

    return {
        usuarios,
        loading,
        error,
        incluirBajas,
        // setIncluirBajas es el setter de useState: React garantiza que su
        // identidad es estable entre renders, no necesita useCallback propio.
        setIncluirBajas,
        reload,
        crearUsuario,
        actualizarUsuario,
        darDeBaja,
        restaurarUsuario,
    };
};
